// The processors picked on a RoPA form, each with its agreement standing (backend lib/dataProtectionLinks.ts
// processorAgreementView, served by GET /gdpr/processor-standing for saved and unsaved picks alike), and the two
// ways to settle it without leaving the form:
//
//   - "Add a processing agreement" while the supplier has none in force: a SupplierContract of kind "dpa", written
//     through the supplier register's own contract route (tprm:write on the server), terms three-state;
//   - "Link an existing agreement" while one is in force but the Art. 28(3) items are not all carried: record which
//     of them that agreement carries, through the same route.
//
// A member without tprm:write sees the standing and who can add one, never a form that would be refused. Nothing
// here decides a standing: an unanswered term is never present, so "in force" appears only once every Art. 28(3)
// item is answered yes on an agreement in force. Contract: docs/CROSS-REFERENCING.md "Data protection and AI
// registers read together".

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiErrorMessage } from "../api/client";
import { theme, btnSecondary, inputStyle } from "../theme";
import { useCurrentUser } from "../auth/useCurrentUser";
import { holdsPermission, whoCan } from "../lib/permissions";
import { ModalHeader, ModalOverlay } from "../pages/resilience/shared";
import { SupplierContractForm, emptyContract, toContractForm, type ContractForm, type SupplierContractRow } from "./SupplierContractForm";

type DpaStanding = "in_force" | "terms_missing" | "terms_unanswered" | "no_agreement";

export interface ProcessorAgreementView {
  supplierId: string;
  name: string;
  isDataProcessor: boolean;
  standing: DpaStanding;
  art28Missing: string[];
  dataLocations: string[];
  inForce: Array<{ id: string; title: string; kind: string }>;
  offer: { add: boolean; link: boolean };
}

export const DPA_STANDING_VIEW: Record<DpaStanding, { label: string; bg: string; text: string }> = {
  in_force:         { label: "Processor terms in force",        bg: "var(--c-successBg)", text: "var(--c-successText)" },
  terms_missing:    { label: "Agreement lacks processor terms", bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  terms_unanswered: { label: "Processor terms not checked",     bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  no_agreement:     { label: "No agreement in force",           bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
};

const PROCESSOR_REL = { isDataProcessor: true, ropaProcessor: true, riskTier: "", deliversImportantService: false };

/** Reads the standing of the processors picked, again whenever the pick changes or `refreshKey` moves. */
export function useProcessorStandings(supplierIds: readonly string[], refreshKey = 0) {
  const [data, setData] = useState<{ processors: ProcessorAgreementView[]; tprmEnabled: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = [...supplierIds].sort().join(",");
  const load = useCallback(() => {
    if (!key) { setData({ processors: [], tprmEnabled: false }); setError(null); return; }
    api.get<{ processors: ProcessorAgreementView[]; tprmEnabled: boolean }>(`/gdpr/processor-standing?supplierIds=${encodeURIComponent(key)}`)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "Could not read the processors' agreements")));
  }, [key]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return { data, error, reload: load };
}

type Dialog =
  | { mode: "add"; processor: ProcessorAgreementView; form: ContractForm }
  | { mode: "link"; processor: ProcessorAgreementView; contractId: string; form: ContractForm };

export function RopaProcessorAgreements({ supplierIds, onChanged }: {
  supplierIds: readonly string[];
  /** After an agreement was added or its terms recorded: the RoPA list, its linkage and attention items are read again. */
  onChanged?: () => void;
}) {
  const { user } = useCurrentUser();
  const canWriteSuppliers = holdsPermission(user?.permissions, "tprm:write");
  const { data, error, reload } = useProcessorStandings(supplierIds);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  if (!supplierIds.length) return null;
  if (error) return <div style={{ fontSize: 12, color: "var(--c-dangerText)" }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 12, color: theme.color.muted }}>Reading the processors' agreements…</div>;

  async function openLink(p: ProcessorAgreementView) {
    const contractId = picked[p.supplierId] ?? p.inForce[0]?.id;
    if (!contractId) return;
    setLoadError(null);
    try {
      const r = await api.get<{ contracts: SupplierContractRow[] }>(`/tprm/suppliers/${p.supplierId}/contracts`);
      const c = (r.contracts ?? []).find((x) => x.id === contractId);
      if (!c) { setLoadError("That agreement is no longer on the supplier."); return; }
      setDialog({ mode: "link", processor: p, contractId, form: toContractForm(c) });
    } catch (e) { setLoadError(apiErrorMessage(e, "Could not open the agreement")); }
  }

  function saved(warning?: string) {
    setDialog(null);
    setNotice(warning ?? null);
    reload();
    onChanged?.();
  }

  const offered = data.processors.some((p) => p.offer.add || p.offer.link);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {data.processors.map((p) => {
        const v = DPA_STANDING_VIEW[p.standing];
        return (
          <div key={p.supplierId} style={{ display: "grid", gap: 6, padding: "8px 10px", border: "1px solid var(--c-border)", borderRadius: 3 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>{p.name}</span>
              <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 2, fontSize: 11, fontWeight: 600, background: v.bg, color: v.text }}>{v.label}</span>
              {!p.isDataProcessor && <span style={{ fontSize: 12, color: "var(--c-warningText)" }}>not marked as a data processor</span>}
            </div>
            {p.standing !== "in_force" && p.standing !== "no_agreement" && p.art28Missing.length > 0 && (
              <div style={{ fontSize: 12, color: theme.color.muted, lineHeight: 1.5 }}>
                Not carried or not checked on the agreements in force: {p.art28Missing.join("; ")}.
              </div>
            )}
            {canWriteSuppliers && data.tprmEnabled && (p.offer.add || p.offer.link) && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {p.offer.add && (
                  <button type="button" style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}
                    onClick={() => setDialog({ mode: "add", processor: p, form: { ...emptyContract, kind: "dpa", title: `Data processing agreement — ${p.name}` } })}>
                    Add a processing agreement
                  </button>
                )}
                {p.offer.link && (
                  <>
                    {p.inForce.length > 1 && (
                      <select aria-label={`Agreement in force with ${p.name}`} value={picked[p.supplierId] ?? p.inForce[0].id}
                        onChange={(e) => setPicked((cur) => ({ ...cur, [p.supplierId]: e.target.value }))} style={{ ...inputStyle, fontSize: 12, width: "auto" }}>
                        {p.inForce.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                      </select>
                    )}
                    <button type="button" style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }} onClick={() => openLink(p)}
                      title="Record which Art. 28(3) items this agreement carries">
                      Link an existing agreement{p.inForce.length === 1 ? `: ${p.inForce[0].title}` : ""}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
      {offered && !canWriteSuppliers && (
        <div style={{ fontSize: 12, color: theme.color.subtle }}>
          Processing agreements are kept on each supplier in the supplier register. {whoCan("tprm:write", "add one or record its terms")}
        </div>
      )}
      {offered && canWriteSuppliers && !data.tprmEnabled && (
        <div style={{ fontSize: 12, color: theme.color.subtle }}>
          Processing agreements are kept in the supplier register, which is not switched on for this organisation (the TPRM add-on).
        </div>
      )}
      {loadError && <div role="alert" style={{ fontSize: 12, color: "var(--c-dangerText)" }}>{loadError}</div>}
      {notice && <div role="status" style={{ fontSize: 12, color: "var(--c-warningText)" }}>{notice}</div>}
      {dialog && createPortal(
        <ModalOverlay onClose={() => setDialog(null)} wide>
          <ModalHeader title={dialog.mode === "add" ? `Add a processing agreement with ${dialog.processor.name}` : `Record the processor terms: ${dialog.form.title}`} onClose={() => setDialog(null)} />
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 10, lineHeight: 1.5 }}>
              {dialog.mode === "add"
                ? "Recorded on the supplier in the supplier register, where every processing activity using this processor reads it. Answer each Art. 28(3) item from the signed agreement: one left \"Not answered\" keeps the agreement reading as not checked."
                : "Answer each Art. 28(3) item from this agreement. The processor's terms read as in force only once every item is answered \"In the agreement\" on an agreement in force."}
            </div>
            <SupplierContractForm
              supplierId={dialog.processor.supplierId}
              contractId={dialog.mode === "link" ? dialog.contractId : null}
              initial={dialog.form}
              rel={PROCESSOR_REL}
              processorFirst
              offerSignedCopy={dialog.mode === "add"}
              kinds={dialog.mode === "add" ? ["dpa"] : undefined}
              onSaved={(_id, warning) => saved(warning)}
              onCancel={() => setDialog(null)}
            />
          </div>
        </ModalOverlay>,
        document.body,
      )}
    </div>
  );
}
